
import React, { useState, useEffect, useMemo, useCallback, forwardRef, useRef } from 'react';
import { Row, Col, Spin, Form, Tooltip } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { MODAL_SIZE_EXTRA_LARGE } from 'constants/Layout';
import _, { noop, isEmpty, isUndefined, isNumber, find, uniq, set, flatten, uniqBy } from 'lodash';
import Vocabulary from 'components/Vocabulary';
import BaseModal from 'components/BaseModal';
import UserAPI from 'restAPI/UserAPI';
import { hasPermission, isSuperOrTenantAdmin, isSuperOrTenantAdminOrTenantRole } from 'utils/authService';
import { Admin, PermissionSetMap, ROLE_SUPERADMIN, ROLE_TENANTADMIN } from 'constants/RBACConstants';
import AppLoader from 'components/AppLoader';
import { generateFormFields, parseSelectOptions, FieldTypes } from "utils/formUtils";
import { checkForOutOfBox } from '../utils';
import message from 'components/CustomMessage';
import TeamsAPI from 'restAPI/TeamsAPI';
import BusinessUnitAPI from 'restAPI/BusinessUnitAPI';
import { InfoIcon } from 'components/Icons';
import {ROLE_TENANT, ROLE_TEAMS, ROLE_SCOPE, emailFieldRules} from 'views/IAM/Users/UserTeamsConstant'
import { useActiveUserContext } from 'contexts/ActiveUserContext';
import { useAutoFocus } from 'hooks/useFocusHelpers';

const initialValues = {
    email: '',
    tenantRole: '',
    disableLogin: false,
    teamInfo: [{
      teamName: '',
      teamId: undefined,
      properties: [],
      role: '',
      canBeModified: true
    }]
}

const setInitialFormData = (mode, user, preFillEmail) => {
  let source = Object.assign({}, initialValues);
  if (mode) {
    source = {...user};
    if (source.defaultBu) {
      source.defaultBU = user.defaultBu.id;
    }
    source.tenantRole = user.tenantRole === ROLE_TENANTADMIN ? Admin : user.tenantRole;
    if (isEmpty(user.teamInfo)) {
      const [d] = initialValues.teamInfo;
      set(source, 'teamInfo', [{...d, role: ''}]);
    }
  }
  source.email = preFillEmail || source.email;
  source.disableLogin = source.disableLogin?? false;
  return source;
}

export default forwardRef(function UserForm({ afterClose = noop, user, activeUser, ...restProps }, ref) {
  const [loading, setLoading] = useState(false);
  const [form] = useForm();
  const editMode = !isEmpty(user);
  const initialFormValues = setInitialFormData(editMode, user, restProps?.preFillEmail);
  const [selectedTeam, setSelectedTeam] = useState([])
  const [rolesMapping, setRolesMapping] = useState({});
  const [teamsOptions, setTeamOptions] = useState([]);
	const [tenantRole, setTenantRole] = useState(user?.tenantRole ? ROLE_TENANT : ROLE_TEAMS);
  const [tenantRoleOpts, setTenantRoleOpts] = useState([]);
	const tenantLevel = tenantRole === ROLE_TENANT;
  const [ userList, setUserList ] = useState();
  const createdUser = useRef();
  const loggedInUser = useActiveUserContext();
  const validateAPIRef = useRef();
  const emailInputFieldRef = useAutoFocus();

  useEffect(
    () => {
      if (isUndefined(userList) && isEmpty(restProps.userList)) {
        const { promise: promise1, cancel: cancel1 } = UserAPI.getAll();
        const { promise: promise2, cancel: cancel2 } = UserAPI.getUsers({
          filters: { email: [loggedInUser.email] },
        })
        const promiseArr = [promise1, promise2];
        Promise.allSettled(promiseArr).then(responses => {
          const [users, activeUserInfo] = responses;
          const options = (users.value || []).map(user => ({ ...user, id: user.id, name: user.displayName }));
          setUserList(options);
          const userDetail = activeUserInfo.value.data.content[0];
          if (!isEmpty(userDetail)) { // merge the active user with the teamInfo details fetch from the user list
            Object.assign(activeUser, userDetail);
          }
        })
        
        return () => {
          cancel1();
          cancel2();
        };
      } else {
        !isEmpty(restProps.userList) && setUserList(restProps.userList);
      }
  },[restProps.userList, userList, activeUser, loggedInUser.email])

  useEffect(
    () => {
      if (tenantLevel && !tenantRoleOpts.length) {
        const { promise, cancel } = UserAPI.getAssignableRolesForBU();
        let allRolesCancel;
        promise.then(roles => {
          const userHadTenantRole = editMode && user?.tenantRole;
          // if the user is initially has a tenant role and in edit mode insure the current assigned role id there in dropdown even if it is restricted
          if(userHadTenantRole){
          const hasCurrentRole = roles.some(r => r.role === initialFormValues.tenantRole);
          if (!hasCurrentRole) {
            const { promise: allRolesPromise, cancel: cancelAllRoles } = UserAPI.getRoles();
            allRolesCancel = cancelAllRoles;
            allRolesPromise.then(allRoles => {
              const currentRole = allRoles.find(r => r.role === user.tenantRole);
              setTenantRoleOpts([...roles, currentRole]);
            })
          } else {
            setTenantRoleOpts(roles);
          }
        }else{
          setTenantRoleOpts(roles);
        }
        }).catch((err) => {
          console.log(err);
        });
        return () => {
          cancel();
          if(allRolesCancel){
          allRolesCancel();
          }
        };
      }
  },[tenantRoleOpts, tenantLevel,editMode,user])

  const activeUserTeams = useMemo(() => {
    return !isEmpty(user?.teamInfo)
      ? (user.teamInfo || []).map(team => ({ id: team.id || team.teamId, name: team.name || team.teamName}))
      : [];
	}, [user]);

  const activeUserRolesMap = useCallback(
    (options) => {
      const mapping = {};
      options.forEach((option) => {
        mapping[option.teamId] = option;
      })
      return mapping
  }, [])

  const activeUserRole = useMemo(() => {
		return !isEmpty(user?.teamInfo)
      ?  activeUserRolesMap(user.teamInfo || [])
      : [];
	}, [user, activeUserRolesMap]);

  const fetchTeams = useCallback(() => {
    const {promise, cancel} = TeamsAPI.getUserTeams();
    promise.then(setTeamOptions, err => console.log(err))
    return cancel;
  }, [])

  useEffect(() => {
    return fetchTeams();
  }, [fetchTeams]);

  useEffect(() => {
    setLoading(true);
    const promiseArr = [];
    const rolesMapping = {};
    const teamIds = [];
    const disabledTeamIds = [];
    const teamsNeedingAllRoles = [];
    let allRolesCancel;

    activeUserTeams.forEach(team => {
      if (checkForAdminOrModifyPermission({teamId: team.id, ...team}, activeUser, editMode)) { // If the user has permission for the team than we trigger the API to get roles
        promiseArr.push(UserAPI.getAssignableRolesForBU(team.id, 'teamId'))
      } else {
        const { teamInfo=[] } = user;
        const obj = find(teamInfo, t => t.teamId === team.id);
        if (obj) { // For disabled teams, we need to fetch full role details to show correct out-of-box status
          disabledTeamIds.push({ teamId: team.id, roleName: obj.role });
          teamIds.push(team.id);
        }
      }
    });
      Promise.allSettled(promiseArr.map(p => p.promise)).then(res => {
        activeUserTeams.forEach((team) => {
          if (!teamIds.includes(team.id)) {
            const data = res.shift();
            const teamRoles = data.value || [];
            // In edit mode, ensure user's current role for this team is in the list
            if (editMode) {
              const userTeamInfo = user.teamInfo.find(t => (t.teamId || t.id) === team.id);
              const hasCurrentRole = teamRoles.some(r => r.role === userTeamInfo.role);
                if (!hasCurrentRole) {
                  teamsNeedingAllRoles.push({ teamId: team.id, teamRoles, roleName: userTeamInfo.role });
                } else {
                  rolesMapping[team.id] = teamRoles;
                }
            } else {
              rolesMapping[team.id] = teamRoles;
            }
            teamIds.push(team.id);
          }
        });
        const needsAllRoles = editMode && (disabledTeamIds.length > 0 || teamsNeedingAllRoles.length > 0);
        if (needsAllRoles) {
          const { promise: allRolesPromise, cancel: cancelAllRoles } = UserAPI.getRoles();
          allRolesCancel = cancelAllRoles;
          allRolesPromise.then(allRoles => {
            teamsNeedingAllRoles.forEach(({ teamId, teamRoles, roleName }) => {
              const currentRole = allRoles.find(r => r.role === roleName);
              rolesMapping[teamId] = currentRole ? [...teamRoles, currentRole] : teamRoles;
            });
            disabledTeamIds.forEach(({ teamId, roleName }) => {
              const fullRoleObject = allRoles.find(r => r.role === roleName);
              rolesMapping[teamId] = fullRoleObject ? [fullRoleObject] : [{ role: roleName }];
            });
            setRolesMapping({...rolesMapping});
            setSelectedTeam((selectedTeam) => _.isEmpty(teamIds) && _.isEmpty(selectedTeam) ? selectedTeam : teamIds);
            setLoading(false);
          }).catch((err) => {
            setLoading(false);
          })
        } else {
          setRolesMapping(rolesMapping);
          setSelectedTeam((selectedTeam) => _.isEmpty(teamIds) && _.isEmpty(selectedTeam) ? selectedTeam : teamIds);
          setLoading(false);
        }
      })
      return () => {
        promiseArr.forEach(p => p.cancel());
        if(allRolesCancel){
          allRolesCancel();
        }
      };
  }, [activeUserTeams, activeUser, editMode, user]);

  const fetchRole = useCallback(
    async (teamId) => {
      try {
        const {promise, cancel} = await UserAPI.getAssignableRolesForBU(teamId, 'teamId')
        promise.then(roles => {
          setRolesMapping({...rolesMapping,[teamId]: roles});
        });
        return cancel;
      } catch (e) {
        console.log(e)
        setRolesMapping({...rolesMapping,[teamId]: []});
      }
  }, [rolesMapping]);

  const onRemoveItem = useCallback(
    () => {
      const configs = form.getFieldValue("teamInfo") || [];
      const selectedTeams = configs.filter(config => config?.teamId).map(config => config.teamId)
      setSelectedTeam(selectedTeams)
  }, [form])

  const handleTeamChange = useCallback(
    async (value, field) => {
      form.setFieldsValue({ defaultBU: null });
      const { key } = field;
      selectedTeam[key] = value;
      const newValues = form.getFieldValue("teamInfo") || [];
      const optionSelected = teamsOptions.find(teamOpt => teamOpt.id === value);
      setSelectedTeam(uniq(selectedTeam));
      if (newValues[key]) {
        newValues[key].teamName = optionSelected.name;
      }
      setRolesMapping({...rolesMapping, [value]: null});
      fetchRole(value);
      form.setFieldsValue({ teamInfo: newValues });
      // The issue is that when 2+ team row is added and then you delete the previous team row
      // and in next one you update the selection, the previouly selected value is not enabled
      // This updates the team selection data.
      onRemoveItem();
    },
    [selectedTeam, form, teamsOptions, fetchRole, rolesMapping, onRemoveItem],
  );

  const fetchBU = useCallback(() => {
    if (tenantLevel) {
      return BusinessUnitAPI.getAll();
    }
    if (_.isEmpty(selectedTeam)) {
      return { promise: Promise.resolve([]), cancel: _.noop };
    }

    let cancelAPI = _.noop;
    const promise = new Promise((resolve, reject) => {
     const { promise: teamsPromise, cancel } = TeamsAPI.getListing({ filters: { name: selectedTeam }, page: 0, size: selectedTeam.length });
    
     cancelAPI = cancel;
    
     teamsPromise
      .then(data => {
        const isTeamWithAllBUAccessPresent = _.some(data.content, (entry) => entry.accessOnAllBusinessUnits);
        if (isTeamWithAllBUAccessPresent) {
          const { promise: buPromise, cancel: buCancel } = BusinessUnitAPI.getAll();
          cancelAPI = buCancel;
          return buPromise;
        } else {
          const processedData = _.uniqBy(
            _.flatten(_.map(data.content, (entry) => _.map(entry.properties, (prop) => prop.businessUnit))), 'id');
          return Promise.resolve(processedData);
        }
      })
      .then(resolve)
      .catch(reject);
  });
  
  return { promise, cancel: () => cancelAPI() };
}, [tenantLevel, selectedTeam]);

  const isArmorCodeUser = loggedInUser.email.split("@")[1] === "armorcode.io";

  const fields = useMemo(() => (
    [
      {
        name: 'email',
        colSpan: 8,
        label: "Email",
        placeholder: 'Enter email.',
        rules: emailFieldRules(editMode, validateAPIRef),
        props: {
          disabled: editMode,
          ref: emailInputFieldRef,
          validateFirst: true,
        }
      },
      {
        name: 'disableLogin',
        colSpan: 24,
        label: <>Disable Login <Tooltip title={"If disabled login is checked then user won't be able to login"}><InfoIcon className="m-l-xs" /></Tooltip></>,
        type: FieldTypes.BOOLEAN,
        props: {
          disabled: !(editMode ? hasPermission(PermissionSetMap.UPDATE_USER) : hasPermission(PermissionSetMap.CREATE_USER))
        },
        inline: true
      },
      ...(!editMode && isArmorCodeUser ? [{
        name: 'passwordBasedLogin',
        colSpan: 24,
        label: "Password Based Login",
        type: FieldTypes.BOOLEAN,
        inline: true
      }] : []),
      {
        name: 'heading',
        type: FieldTypes.LABEL,
        value: (<>Role<Tooltip title={<>User with Account Level role will have access to all <Vocabulary name="team" isPlural={true} isLowerCase={true} />  of that account.</>}> <InfoIcon className="m-l-xs" /> </Tooltip></> ),
        className: 'm-b-0'
      },
      {
        name: 'roleScope',
        type: FieldTypes.RADIO,
        options: [ROLE_SCOPE.TENANT, ROLE_SCOPE.TEAMS],
        props: {
          onChange: (e) => {
            form.setFieldsValue({ defaultBU: null });
            setTenantRole(e.target.value);
          },
          defaultValue: tenantRole
        },
        disabled: !checkRoleScopeAccess(user)
      },
      ...(tenantLevel
        ? [{
          name: 'tenantRole',
          type: FieldTypes.SELECT,
          colSpan: 10,
          label: "Role",
          options: getRoleOptions(tenantRoleOpts),
          required: true,
          props: {
            optionFilterProp:"value",
          }
        }]
        : [
            {
              name: 'teamInfo',
              label: <>Configure <Vocabulary name="team" isPlural={true} /></>,
              form: form,
              type: FieldTypes.LIST,
              buttonLabel: <>Add <Vocabulary name="team" /></>,
              onRemoveItem: onRemoveItem,
              addButtonDisabled: checkAvailableOptionsAreSelected(selectedTeam, teamsOptions, activeUserTeams),
              fieldRows: [
                [
                  {
                    name: 'teamId',
                    type: FieldTypes.SELECT,
                    colSpan: 10,
                    label: <Vocabulary name="team" />,
                    props: {
                        getFormListOptions: (fieldKey) => {
                          const options = teamsOptions.map(teamOpt => { return {label: teamOpt.name, value: teamOpt.id, disabled: selectedTeam.includes(teamOpt.id)} });
                          const activeTeam = activeUserTeams[fieldKey];
                          if (activeTeam) {
                            const hasTeam =  options.find(opt => opt.label === activeTeam.name);
                            if (!hasTeam) {
                              const [parseOpt] = parseSelectOptions([activeTeam]);
                              options.push({...parseOpt, disabled: true});
                            }
                          }
                          return options;
                        },
                        onChange: (value, field) => handleTeamChange(value, field),
                        setExtraProps: (field, fieldKey) => {
                          const { name } = fieldKey; // Do not use `key`, use `name` instead. key stays the same for lifecycle, where as name gives the current row position
                          const fields = form.getFieldValue("teamInfo") || [];
                          const disabledRow = !checkForAdminOrModifyPermission(fields[name], activeUser, editMode)
                          return { disabled: disabledRow};
                        }
                    },
                    rules: [{ required: true, message: <><Vocabulary name="team" /> is required</>}]
                  },
                  {
                    name: 'role',
                    type: FieldTypes.SELECT,
                    colSpan: 12,
                    label: 'Role',
                    props: {
                      optionFilterProp:"value",
                      getFormListOptions: (fieldKey) => {
                        const teamInfo = form.getFieldValue("teamInfo") || [];
                        const teamId = teamInfo[fieldKey]?.teamId;
                        const options = rolesMapping[teamId] || [];
                        const activeRole = activeUserRole[teamId];
                        if (activeRole) {
                          const hasOpts = options.find(option => option.role === activeRole.role);
                          if (!hasOpts) {
                            options.push({...activeRole, disabled: true});
                          }
                        }
                        return getRoleOptions(options);
                      },
                      setExtraProps: (field, fieldKey) => {
                        const { name } = fieldKey;
                          const fields = form.getFieldValue("teamInfo") || [];
                          const disabledRow = !checkForAdminOrModifyPermission(fields[name], activeUser, editMode)
                          return { disabled: disabledRow};
                      }
                    },
                    rules: [{ required: true, message: "Role is required"}]
                  }
                ]
              ]
            }
        ]),
        {
          name: 'defaultBU',
          type: FieldTypes.SELECT,
          colSpan: 10,
          label: "Assign Default Organization",
          props: {
            api: fetchBU,
          }
        }
    ]
  ), [teamsOptions, rolesMapping, selectedTeam, handleTeamChange, form, editMode, activeUser, activeUserRole, activeUserTeams, tenantLevel, tenantRole, tenantRoleOpts, user, onRemoveItem, fetchBU, isArmorCodeUser, emailInputFieldRef])

  const onSubmit = async () => {
    const valid = await form.validateFields();
    if (valid) {
      setLoading(true);
      try {
        const data = form.getFieldsValue();
        const defaultBU = data.defaultBU;
        const methodType = editMode ? 'updateUser' : 'addUser';
        if (editMode) {
          data.id = user.userId;
          (data.teamInfo || []).forEach(info => {
            const hasTeam = initialFormValues.teamInfo.find(team => team.teamId === info.teamId);
            if (!hasTeam) {
              info.properties = [];
            }
          })
        }
        if (tenantLevel) {
          data.teamInfo = null;
        } else {
          delete data.tenantRole ;
        }
        delete data.heading;
        delete data.roleScope;
        delete data.defaultBU;
        const _user = await UserAPI[methodType](data).promise;
        console.log('_user', _user)
        if (defaultBU) {
          await UserAPI.setDefaultBU(defaultBU, editMode ?user.userId : _user.id);
        }
        createdUser.current = _user;
        message.success(`User ${editMode ? 'Updated' : 'Created'} successfully.`);
      } catch (e) {
        setLoading(false);
        console.error(e);
        throw e;
      }
    }
  }

  const baseModalProps = {
		title: editMode ? 'Edit User' : 'Add User',
		width: MODAL_SIZE_EXTRA_LARGE,
		okText: editMode ? 'Save' : 'Add New User',
		onOk: async () => {
			await onSubmit();
		}
	};
  return (
      <BaseModal
        ref={ref}
        modalProps={baseModalProps}
        afterClose={() => afterClose(createdUser.current)}
        >
        <Spin spinning={loading} indicator={<AppLoader style={{ marginTop: 0 }} />}>
            <Row><Col span={24}>Add multiple <Vocabulary name="team" isPlural={true} />, while adding the new user. </Col></Row>
            <Row>
              <Col span={24}>
                <ul>
                  <li>Only one role per <Vocabulary name="team" isLowerCase={true} /> can be added</li>
                  <li>You can not edit the records which are disabled, as you don't have access to that <Vocabulary name="team" /></li>
                </ul>
              </Col>
            </Row>
          <Form
            layout="vertical"
            form={form}
            initialValues={initialFormValues}
          >
            {generateFormFields(fields)}
          </Form>
        </Spin>
      </BaseModal>
  );
});

export function checkForAdminOrModifyPermission(currentFieldValue, selfData, editUser) {
	if(isUndefined(currentFieldValue) || !isNumber(currentFieldValue.teamId) || isSuperOrTenantAdmin() || isSuperOrTenantAdminOrTenantRole() || !selfData) { // This means no Team is selected
		return true;
	}

	const myTeamLevelData = find(selfData?.teamInfo, { teamId: currentFieldValue.teamId });
	const role = selfData?.tenantRole || myTeamLevelData?.role || '';

	if (hasPermission(editUser ? PermissionSetMap.UPDATE_USER : PermissionSetMap.CREATE_USER) && role) {
		if (currentFieldValue.canBeModified || isUndefined(currentFieldValue.canBeModified)) { // undefined means new Team is being added
			return true;
		}
	}
	return ([ROLE_SUPERADMIN, ROLE_TENANTADMIN, Admin].indexOf(role) !== -1);
}

export function getRoleOptions(roles = []) {
	return roles.map(role => ({ label: checkForOutOfBox(role), value: role.role, roleId: role.id }));
}

function checkRoleScopeAccess(selfData) {
	return isSuperOrTenantAdminOrTenantRole() || !isEmpty(selfData?.tenantRole);
}

export function checkAvailableOptionsAreSelected(selected, options, activeOptions) {
  if (isEmpty(selected)) {
    return false;
  }
  const mergeOption = uniqBy([...options, ...flatten(activeOptions)] , 'id')
  return mergeOption.every(option => {
    return selected.some(select => select === option.id);
  })
}